import {
  TransactionModel,
  LineItemModel,
  TransitionModel,
  HistoricalTransitionModel,
  TransactionMetaDataModel,
  ShipmentParcelModel,
  TransactionProtectedDataModel,
  ParcelStatusModel,
  PaymentMethodModel,
  PaymentMethodIssuerModel,
  ROLE,
} from '@loavies/c2c-models'
import { mapListing } from './listing-mappers'
import { mapPrice } from './price-mappers'
import { mapUser, mapUserAddress } from './user-mappers'
import { mapReviews } from './review-mappers'

/**
 * @param {Array<Object>} transactions
 * @param {string} [userId]
 * @return {loavies.models.transaction.TransactionModel[]}
 */
export function mapTransactions(transactions, userId) {
  return transactions.map(transaction => mapTransaction(transaction, userId))
}

/**
 * @param {Array<Object>} lineItems
 * @return {loavies.models.transaction.LineItemModel[]}
 */
export function mapLineItems(lineItems) {
  return lineItems.map(lineItem => mapLineItem(lineItem))
}

/**
 * @param {Object} transaction
 * @param {string} [userId]
 * @return {loavies.models.transaction.TransactionModel}
 */
export function mapTransaction(transaction, userId) {
  const transitionHistory = mapTransitionHistory(transaction.attributes.transitions)

  return new TransactionModel({
    id: transaction.id.uuid,
    createdAt: transaction.attributes.createdAt,
    lineItems: mapLineItems(transaction.attributes.lineItems),
    metaData: mapMetaData(transaction.attributes.metadata),
    payinTotal: mapPrice(transaction.attributes.payinTotal),
    payoutTotal: mapPrice(transaction.attributes.payoutTotal),
    protectedData: mapProtectedData(transaction.attributes.protectedData),
    userRole: userId ? mapUserRole(transaction, userId) : undefined,
    listing: transaction.listing.attributes.state ? mapListing(transaction.listing) : null,
    customer: mapUser(transaction.customer),
    provider: mapUser(transaction.provider),
    transitionHistory,
    lastHistoricalTransition: transitionHistory.find(transition => transition.name === transaction.attributes.lastTransition),
    lastTransitionedAt: transaction.attributes.lastTransitionedAt,
    nextTransitions: null,
    reviews: mapReviews(transaction.reviews),
  })
}

/**
 * @param {Object} lineItem
 * @return {loavies.models.transaction.LineItemModel}
 */
export function mapLineItem(lineItem) {
  return new LineItemModel({
    code: lineItem.code,
    unitPrice: mapPrice(lineItem.unitPrice),
    quantity: lineItem.quantity,
    includeFor: lineItem.includeFor,
    lineTotal: mapPrice(lineItem.lineTotal),
  })
}

/**
 * @param {Object} transaction
 * @param {string} userId
 * @return {string}
 */
export function mapUserRole(transaction, userId) {
  const isProvider = transaction.provider.id.uuid === userId
  return isProvider ? ROLE.PROVIDER : ROLE.CUSTOMER
}

/**
 * @param {Array} transitionHistory
 * @returns {loavies.models.transaction.HistoricalTransitionModel[]}
 */
export function mapTransitionHistory(transitionHistory) {
  return transitionHistory.map(transition => mapHistoricalTransition(transition))
}

/**
 * @param {Object} transition
 * @returns {loavies.models.transaction.HistoricalTransitionModel}
 */
export function mapHistoricalTransition(transition) {
  return new HistoricalTransitionModel({
    name: transition.transition,
    actor: transition.by,
    createdAt: transition.createdAt,
  })
}

/**
 * @param {Array} transitions
 * @returns {loavies.models.transaction.TransitionModel[]}
 */
export function mapTransitions(transitions) {
  return transitions.map(transition => mapTransition(transition))
}

/**
 * @param {Object} transition
 * @returns {loavies.models.transaction.TransitionModel}
 */
export function mapTransition(transition) {
  return new TransitionModel({
    id: transition.id.uuid,
    name: transition.attributes.name,
    actors: transition.attributes.actor,
    actions: transition.attributes.actions,
    requiredParameters: mapTransitionParameters(transition.attributes.params.req),
    optionalParameters: mapTransitionParameters(transition.attributes.params.opt),
  })
}

/**
 * @param {Object} parameters
 * @returns {null|Object}
 */
export function mapTransitionParameters(parameters) {
  if (Object.keys(parameters)) {
    return parameters
  }

  return null
}

/**
 * @param {Object} metaData
 * @return {loavies.models.transaction.TransactionMetaDataModel}
 */
export function mapMetaData(metaData) {
  return new TransactionMetaDataModel({
    parcels: metaData.parcels?.map(parcel => mapShipmentParcel(parcel)),
    notificationsSent: metaData.notificationsSent,
    orderId: metaData.orderId,
  })
}

/**
 * @param {Object} parcel
 * @return {loavies.models.shipment.ShipmentParcelModel}
 */
export function mapShipmentParcel(parcel) {
  return new ShipmentParcelModel({
    id: parcel.id,
    trackingCodes: parcel.trackingCodes,
    labelType: parcel.labelType,
    qr: parcel.qr,
    qrCodeImage: parcel.qrCodeImage,
    trackingUrl: parcel.trackingUrl,
    size: parcel.size,
    weight: parcel.weight,
    status: parcel.status ? mapParcelStatus(parcel.status) : undefined,
  })
}

/**
 * @param {Object} protectedData
 * @return {loavies.models.transaction.TransactionProtectedDataModel}
 */
export function mapProtectedData(protectedData) {
  return new TransactionProtectedDataModel({
    deliveryAddress: protectedData.deliveryAddress && mapUserAddress(protectedData.deliveryAddress),
    selectedPaymentMethod: protectedData.selectedPaymentMethod && mapSelectedPaymentMethod(protectedData.selectedPaymentMethod),
  })
}

/**
 * @param {Object} parcelStatus
 * @returns {loavies.models.shipment.ParcelStatusModel}
 */
export function mapParcelStatus(parcelStatus) {
  return new ParcelStatusModel({
    name: parcelStatus.name,
    parcelId: parcelStatus.parcelId,
    createdAt: parcelStatus.createdAt,
    plannedDeliveryTimeframeStart: parcelStatus.plannedDeliveryTimeframeStart,
    plannedDeliveryTimeframeEnd: parcelStatus.plannedDeliveryTimeframeEnd,
    nextPlannedDeliveryTimeframeStart: parcelStatus.nextPlannedDeliveryTimeframeStart,
    nextPlannedDeliveryTimeframeEnd: parcelStatus.nextPlannedDeliveryTimeframeEnd,
  })
}

/**
 * @param {Object} selectedPaymentMethod
 * @return {loavies.models.payment.PaymentMethodModel|loavies.models.payment.PaymentMethodIssuerModel}
 */
export function mapSelectedPaymentMethod(selectedPaymentMethod) {
  if (selectedPaymentMethod.id) {
    return new PaymentMethodIssuerModel({
      id: selectedPaymentMethod.id,
      name: selectedPaymentMethod.name,
      type: selectedPaymentMethod.type,
    })
  }

  return new PaymentMethodModel({
    type: selectedPaymentMethod.type,
    name: selectedPaymentMethod.name,
    issuers: undefined,
  })
}
